import {
  KeyOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Modal } from "antd";
import { useChangePasswordByUser } from "../../query/UserQuery";
import { notify } from "../../utils/toast";

interface ModalChangePasswordProps {
  open: boolean;
  onCancel: () => void;
}

const ModalChangePassword: React.FC<ModalChangePasswordProps> = ({
  open,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const changePasswordMutation = useChangePasswordByUser();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      await changePasswordMutation.mutateAsync({
        password: values.newPassword,
        oldPassword: values.currentPassword,
      });

      notify.success("Đổi mật khẩu thành công");
      form.resetFields();
      onCancel();
    } catch (error) {
      notify.error("Đổi mật khẩu thất bại");
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 text-lg">
          <KeyOutlined className="text-primary" />
          <span>Đổi mật khẩu</span>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      maskClosable={false}
      destroyOnClose
      centered
    >
      <div className="bg-blue-50 dark:bg-[var(--bg-secondary)] border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <SafetyCertificateOutlined className="text-blue-500 text-lg mt-0.5" />
          <div>
            <p className="text-blue-700 font-medium mb-1">
              Lưu ý khi đặt mật khẩu:
            </p>
            <ul className="text-blue-600 text-sm list-disc ml-4 space-y-1">
              <li>Độ dài ít nhất 8 ký tự</li>
              <li>Nên kết hợp chữ hoa, chữ thường, số và ký tự đặc biệt</li>
              <li>Không nên sử dụng thông tin cá nhân dễ đoán</li>
            </ul>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
      >
        <Form.Item
          name="currentPassword"
          label="Mật khẩu hiện tại"
          rules={[
            { required: true, message: "Vui lòng nhập mật khẩu hiện tại" },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Nhập mật khẩu hiện tại"
            size="large"
            className="rounded-lg"
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="Mật khẩu mới"
          rules={[
            { required: true, message: "Vui lòng nhập mật khẩu mới" },
            { min: 8, message: "Mật khẩu phải có ít nhất 8 ký tự" },
            {
              pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/,
              message: "Mật khẩu phải chứa cả chữ và số",
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Nhập mật khẩu mới"
            size="large"
            className="rounded-lg"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Xác nhận mật khẩu"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Vui lòng xác nhận mật khẩu" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Mật khẩu xác nhận không khớp")
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Nhập lại mật khẩu mới"
            size="large"
            className="rounded-lg"
          />
        </Form.Item>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            size="large"
            onClick={() => {
              form.resetFields();
              onCancel();
            }}
            className="min-w-[100px]"
          >
            Hủy bỏ
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            loading={changePasswordMutation.isPending}
            className="min-w-[100px]"
            style={{ background: "var(--primary-color)" }}
          >
            Xác nhận
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ModalChangePassword;
